/**
 * ScenarioSlider — client-side what-if simulator for PCF.
 *
 * Zero backend calls: we recalculate PCF locally from the three contribution
 * slices (energy / process / materials) that the `/pcf/{id}` endpoint already
 * returns. The slider rescales the energy slice (a proxy for "how much you
 * cut or grow consumption") and rescales the denominator (production volume)
 * when the user picks that axis. The result is instant and free.
 */
import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TrendingDown, TrendingUp, SlidersHorizontal } from 'lucide-react';

const AXES = [
  { value: 'energy',     label: 'Energy consumption' },
  { value: 'production', label: 'Production volume' },
];

function formatPct(p) {
  const sign = p > 0 ? '+' : '';
  return `${sign}${p.toFixed(1)}%`;
}

export default function ScenarioSlider({ detail }) {
  const [axis, setAxis] = useState('energy');
  const [factor, setFactor] = useState(1.0); // 0.5x .. 1.5x

  // Contributions come from the `/api/pcf/{id}` response (populated by
  // SimpleDashboard via selected product). If they're missing, we fall back
  // to rescaling the total PCF value directly (simplest heuristic).
  const baseline = Number(detail?.pcf_value) || Number(detail?.pcf) || 0;
  const contributions = detail?.contributions || null;

  const projected = useMemo(() => {
    if (!baseline) return { value: 0, delta: 0 };

    if (contributions && axis === 'energy') {
      const energy = Number(contributions.energy) || 0;
      const process = Number(contributions.process) || 0;
      const materials = Number(contributions.materials) || 0;
      // Rescale only the energy slice.
      const next = (energy * factor) + process + materials;
      return { value: next, delta: ((next - baseline) / baseline) * 100 };
    }

    if (axis === 'production') {
      // Lower production → same energy/process per product → HIGHER PCF/unit.
      // Higher production → LOWER PCF/unit. Inverse relationship.
      if (factor <= 0) return { value: baseline, delta: 0 };
      const next = baseline / factor;
      return { value: next, delta: ((next - baseline) / baseline) * 100 };
    }

    // Fallback: straight rescale of total
    const next = baseline * factor;
    return { value: next, delta: ((next - baseline) / baseline) * 100 };
  }, [baseline, contributions, axis, factor]);

  const axisLabel = AXES.find((a) => a.value === axis)?.label || '';
  const moveLabel = factor === 1 ? 'unchanged' : `${factor < 1 ? 'down' : 'up'} ${(Math.abs(factor - 1) * 100).toFixed(0)}%`;
  const Arrow = projected.delta > 0.05 ? TrendingUp : TrendingDown;
  const arrowColor = projected.delta > 0.05 ? 'text-red-600' : (projected.delta < -0.05 ? 'text-green-600' : 'text-muted-foreground');

  if (!baseline) return null;

  return (
    <Card className="p-6" data-testid="scenario-slider">
      <div className="flex items-center gap-2 mb-4">
        <SlidersHorizontal className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-heading font-semibold">What-if simulator</h3>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider ml-auto">
          runs locally · no AI call
        </span>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Adjust axis</label>
          <Select value={axis} onValueChange={(v) => setAxis(v)}>
            <SelectTrigger data-testid="scenario-axis-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AXES.map((a) => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="block text-xs font-medium text-muted-foreground mt-4 mb-1.5">
            Change factor: <span className="text-foreground font-semibold">{factor.toFixed(2)}×</span>
            <span className="text-muted-foreground"> ({moveLabel})</span>
          </label>
          <input
            type="range"
            min="0.5"
            max="1.5"
            step="0.05"
            value={factor}
            onChange={(e) => setFactor(Number(e.target.value))}
            className="w-full accent-primary"
            data-testid="scenario-factor-slider"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>0.5× (half)</span><span>1.0× (now)</span><span>1.5× (+50%)</span>
          </div>
        </div>

        <div className="flex flex-col justify-center">
          <div className="text-xs text-muted-foreground mb-1">Projected PCF</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-heading font-bold text-primary" data-testid="scenario-projected-value">
              {projected.value.toFixed(2)}
            </span>
            <span className="text-xs text-muted-foreground">kg CO₂e / unit</span>
          </div>
          <div className={`mt-1 inline-flex items-center gap-1.5 text-sm font-medium ${arrowColor}`} data-testid="scenario-projected-delta">
            <Arrow className="w-4 h-4" />
            {formatPct(projected.delta)} vs. current ({baseline.toFixed(2)})
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            If you move <strong>{axisLabel.toLowerCase()}</strong> {moveLabel}, PCF becomes{' '}
            <strong>{projected.value.toFixed(2)}</strong> kg CO₂e / unit.
          </p>
        </div>
      </div>
    </Card>
  );
}
